(function () {
  const ROOT_SEL = "[data-cfgf-root]";
  const TRACK_SEL = "[data-cfgf-track]";
  const PREV_SEL = "[data-cfgf-prev]";
  const NEXT_SEL = "[data-cfgf-next]";
  const SLIDE_SEL = ".cfgf-cart-gifts__slide";

  const SCROLL_END_DEBOUNCE = 140;
  const REINIT_TRIES = 8;
  const REINIT_DELAY = 120;

  function q(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function qa(root, sel) {
    return root ? Array.from(root.querySelectorAll(sel)) : [];
  }

  function getGap(track) {
    const gap = parseFloat(getComputedStyle(track).gap || "0");
    return Number.isFinite(gap) ? gap : 0;
  }

  function getStep(track) {
    const slide = track.querySelector(SLIDE_SEL);
    if (!slide) return 0;
    return slide.getBoundingClientRect().width + getGap(track);
  }

  function setScrollBehavior(track, value) {
    track.style.scrollBehavior = value;
  }

  function goToIndex(track, idx, smooth) {
    const step = track.__cfgfStep || getStep(track);
    if (!step) return;

    setScrollBehavior(track, smooth ? "smooth" : "auto");
    track.scrollLeft = idx * step;
  }

  function getNearestIndex(track) {
    const step = track.__cfgfStep || getStep(track);
    if (!step) return 0;
    return Math.round(track.scrollLeft / step);
  }

  function afterScrollEnd(track, cb) {
    if ("onscrollend" in document) {
      const onEnd = () => {
        track.removeEventListener("scrollend", onEnd);
        cb();
      };
      track.addEventListener("scrollend", onEnd, { once: true });
      return;
    }

    clearTimeout(track.__cfgfScrollEndTimer);

    const onScroll = () => {
      clearTimeout(track.__cfgfScrollEndTimer);
      track.__cfgfScrollEndTimer = setTimeout(() => {
        track.removeEventListener("scroll", onScroll);
        cb();
      }, SCROLL_END_DEBOUNCE);
    };

    track.addEventListener("scroll", onScroll);

    track.__cfgfScrollEndTimer = setTimeout(() => {
      track.removeEventListener("scroll", onScroll);
      cb();
    }, 600);
  }

  function normalizeIfOnClone(track) {
    const n = track.__cfgfRealCount;
    if (!n) return;

    const idx = getNearestIndex(track);

    if (idx < n) {
      goToIndex(track, idx + n, false);
    } else if (idx >= n * 2) {
      goToIndex(track, idx - n, false);
    }
  }

  function removeClones(track) {
    qa(track, `${SLIDE_SEL}[data-clone="1"]`).forEach((el) => el.remove());
  }

  function bindScrollOnce(track) {
    if (track.__cfgfScrollBound) return;
    track.__cfgfScrollBound = true;

    track.addEventListener("scroll", () => {
      track.__cfgfIsScrolling = true;
      clearTimeout(track.__cfgfIsScrollingTimer);
      track.__cfgfIsScrollingTimer = setTimeout(() => {
        track.__cfgfIsScrolling = false;
      }, 180);

      if (track.__cfgfLock) return;

      clearTimeout(track.__cfgfNormalizeTimer);
      track.__cfgfNormalizeTimer = setTimeout(() => {
        normalizeIfOnClone(track);
      }, 80);
    });
  }

  function getRealSlides(track) {
    return qa(track, `${SLIDE_SEL}:not([data-clone="1"])`);
  }

  function hasLoopClones(track, realCount) {
    return qa(track, `${SLIDE_SEL}[data-clone="1"]`).length === realCount * 2;
  }

  function setupTrack(track) {
    if (!track) return;

    const realSlides = getRealSlides(track);

    if (realSlides.length < 2) {
      track.__cfgfRealCount = realSlides.length || 0;
      track.__cfgfStep = 0;
      return;
    }

    if (
      track.__cfgfInited &&
      track.__cfgfRealCount === realSlides.length &&
      hasLoopClones(track, realSlides.length)
    ) {
      track.__cfgfStep = getStep(track);
      bindScrollOnce(track);
      return;
    }

    removeClones(track);

    track.__cfgfInited = true;
    track.__cfgfRealCount = realSlides.length;

    const leadingClones = realSlides.map((slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.clone = "1";
      return clone;
    });
    const trailingClones = realSlides.map((slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.clone = "1";
      return clone;
    });

    leadingClones.reverse().forEach((clone) => {
      track.insertBefore(clone, track.firstChild);
    });
    trailingClones.forEach((clone) => {
      track.appendChild(clone);
    });

    requestAnimationFrame(() => {
      track.__cfgfStep = getStep(track);
      goToIndex(track, realSlides.length, false);
      setScrollBehavior(track, "smooth");
    });

    bindScrollOnce(track);
  }

  function isMobileDropdown() {
    return (
      window.matchMedia("(max-width: 768px)").matches ||
      window.matchMedia("(pointer: coarse)").matches
    );
  }

  function buildVariantDropdown(ddRoot) {
    const select = ddRoot.querySelector("[data-cfgf-variant-select]");
    const panel = ddRoot.querySelector("[data-cfgf-dd-panel]");
    const trigger = ddRoot.querySelector("[data-cfgf-dd-trigger]");
    const chip = ddRoot.querySelector("[data-cfgf-dd-chip]");
    const name = ddRoot.querySelector("[data-cfgf-dd-name]");
    if (!select || !panel || !trigger || !chip || !name) return;

    const getSwatch = (opt) =>
      opt.getAttribute("data-swatch") || opt.getAttribute("data-image") || "";

    const selectUnchanged = ddRoot.__cfgfSelectRef === select;
    const panelHasItems = panel.children && panel.children.length > 0;

    if (selectUnchanged && panelHasItems && ddRoot.__cfgfAbort) {
      const opt = select.options[select.selectedIndex];
      if (opt) {
        chip.style.backgroundImage = `url('${getSwatch(opt)}')`;
        name.textContent = (opt.textContent || "").trim();
      }
      return;
    }

    ddRoot.__cfgfSelectRef = select;

    if (ddRoot.__cfgfAbort) {
      try {
        ddRoot.__cfgfAbort.abort();
      } catch (_) {}
    }

    ddRoot.__cfgfAbort = new AbortController();
    const { signal } = ddRoot.__cfgfAbort;

    const portalState =
      ddRoot.__cfgfPortalState ||
      (ddRoot.__cfgfPortalState = {
        backdrop: null,
        originalParent: null,
        originalNext: null,
      });

    function ensurePortalStateSaved() {
      if (!portalState.originalParent) {
        portalState.originalParent = panel.parentNode;
        portalState.originalNext = panel.nextSibling;
      }
    }

    function unmountPortal() {
      if (portalState.backdrop && portalState.backdrop.parentNode) {
        portalState.backdrop.parentNode.removeChild(portalState.backdrop);
      }

      if (portalState.originalParent) {
        panel.classList.remove("is-portal");
        panel.style.left = "";
        panel.style.width = "";
        panel.style.top = "";
        panel.style.bottom = "";
        panel.style.maxHeight = "";
        portalState.originalParent.insertBefore(panel, portalState.originalNext);
      }
    }

    function close() {
      ddRoot.setAttribute("aria-expanded", "false");
      unmountPortal();
    }

    function updatePortalPlacement() {
      const rect = trigger.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;

      const GAP = 12;
      const OFFSET = 8;
      const MIN_PANEL = 140;

      const spaceBelow = vh - rect.bottom - GAP;
      const spaceAbove = rect.top - GAP;
      const openTop = spaceBelow < MIN_PANEL && spaceAbove > spaceBelow;

      const left = GAP;
      const width = Math.max(280, vw - GAP * 2);

      panel.style.left = left + "px";
      panel.style.width = width + "px";

      const available = Math.max(120, (openTop ? spaceAbove : spaceBelow) - OFFSET);
      panel.style.maxHeight = available + "px";

      if (openTop) {
        panel.style.top = "";
        panel.style.bottom = vh - rect.top + OFFSET + "px";
      } else {
        panel.style.bottom = "";
        panel.style.top = rect.bottom + OFFSET + "px";
      }
    }

    function updateDesktopPlacement() {
      const rect = trigger.getBoundingClientRect();

      const card =
        ddRoot.closest(".cfgf-cart-gifts__card") ||
        ddRoot.closest(".cfgf-cart-gifts__slide") ||
        ddRoot;

      const cardRect = card.getBoundingClientRect();

      const GAP = 8;
      const OFFSET = 6;
      const MIN_PANEL = 120;

      const spaceBelow = cardRect.bottom - rect.bottom - GAP;
      const spaceAbove = rect.top - cardRect.top - GAP;

      const openTop = spaceBelow < MIN_PANEL && spaceAbove > spaceBelow;

      ddRoot.dataset.placement = openTop ? "top" : "bottom";

      const available = Math.max(100, (openTop ? spaceAbove : spaceBelow) - OFFSET);
      panel.style.maxHeight = available + "px";
    }

    function mountPortal() {
      ensurePortalStateSaved();

      if (!portalState.backdrop) {
        portalState.backdrop = document.createElement("div");
        portalState.backdrop.className = "cfgf-variant-dd__backdrop";
      }

      portalState.backdrop.addEventListener("click", close, { signal });

      document.body.appendChild(portalState.backdrop);
      document.body.appendChild(panel);
      panel.classList.add("is-portal");
    }

    function open() {
      if (isMobileDropdown()) {
        mountPortal();
        updatePortalPlacement();
        ddRoot.setAttribute("aria-expanded", "true");
        return;
      }

      unmountPortal();
      updateDesktopPlacement();
      ddRoot.setAttribute("aria-expanded", "true");
    }

    function toggle() {
      ddRoot.getAttribute("aria-expanded") === "true" ? close() : open();
    }

    close();
    panel.innerHTML = "";

    Array.from(select.options).forEach((opt, idx) => {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "cfgf-variant-dd__item";
      item.dataset.idx = String(idx);
      if (opt.disabled) item.disabled = true;

      const img = getSwatch(opt);
      const title = (opt.textContent || "").trim();

      item.innerHTML = `
        <span class="cfgf-variant-dd__chip" style="background-image:url('${img}')"></span>
        <span class="cfgf-variant-dd__name">${title}</span>
      `;

      item.addEventListener(
        "click",
        () => {
          if (item.disabled) return;
          select.selectedIndex = idx;
          close();
          select.dispatchEvent(new Event("change", { bubbles: true }));
        },
        { signal }
      );

      panel.appendChild(item);
    });

    function syncFromSelect() {
      const opt = select.options[select.selectedIndex];
      if (!opt) return;
      chip.style.backgroundImage = `url('${getSwatch(opt)}')`;
      name.textContent = (opt.textContent || "").trim();
    }

    syncFromSelect();
    select.addEventListener("change", syncFromSelect, { signal });

    trigger.addEventListener(
      "click",
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle();
      },
      { signal }
    );

    document.addEventListener(
      "click",
      (e) => {
        if (!ddRoot.contains(e.target) && !panel.contains(e.target)) close();
      },
      { signal }
    );

    document.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape") close();
      },
      { signal }
    );

    window.addEventListener(
      "resize",
      () => {
        if (ddRoot.getAttribute("aria-expanded") !== "true") return;
        isMobileDropdown() ? updatePortalPlacement() : updateDesktopPlacement();
      },
      { signal }
    );

    window.addEventListener(
      "scroll",
      () => {
        if (ddRoot.getAttribute("aria-expanded") !== "true") return;
        isMobileDropdown() ? updatePortalPlacement() : updateDesktopPlacement();
      },
      { signal, capture: true }
    );
  }

  function initVariantDropdowns() {
    document.querySelectorAll("[data-cfgf-dd]").forEach(buildVariantDropdown);
  }

  function initAll() {
    document.querySelectorAll(ROOT_SEL).forEach((root) => {
      const track = q(root, TRACK_SEL);
      setupTrack(track);
    });

    initVariantDropdowns();
    syncAllGiftStates();
  }

  function resetAllTracks() {
    document.querySelectorAll(ROOT_SEL).forEach((root) => {
      const track = q(root, TRACK_SEL);
      if (!track) return;

      removeClones(track);
      track.__cfgfInited = false;
      track.__cfgfRealCount = 0;
      track.__cfgfStep = 0;

      clearTimeout(track.__cfgfNormalizeTimer);
      clearTimeout(track.__cfgfScrollEndTimer);
    });

    document.querySelectorAll("[data-cfgf-dd]").forEach((dd) => {
      if (dd.__cfgfAbort) {
        try {
          dd.__cfgfAbort.abort();
        } catch (_) {}
        dd.__cfgfAbort = null;
      }
      dd.__cfgfSelectRef = null;
    });
  }

  function isReady() {
    const roots = document.querySelectorAll(ROOT_SEL);
    if (!roots.length) return true;

    for (const root of roots) {
      const track = q(root, TRACK_SEL);
      if (!track) continue;

      const realCount = track.__cfgfRealCount || getRealSlides(track).length;
      if (realCount >= 2 && hasLoopClones(track, realCount)) continue;

      return false;
    }

    return true;
  }

  function reinitWithRetries() {
    resetAllTracks();

    let i = 0;
    const tick = () => {
      initAll();

      if (isReady()) return;

      i += 1;
      if (i < REINIT_TRIES) setTimeout(tick, REINIT_DELAY);
    };

    tick();
  }

  function formatMoney(cents, format) {
    const value = typeof cents === "string" ? cents.replace(".", "") : String(cents);
    const number = parseInt(value, 10);
    if (!Number.isFinite(number)) return "";

    function formatWithDelimiters(amount, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ",";
      decimal = decimal || ".";

      const amt = (amount / 100).toFixed(precision);
      const parts = amt.split(".");
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision ? dollars + decimal + parts[1] : dollars;
    }

    const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch ? placeholderMatch[1] : "amount";

    let formatted = "";
    switch (placeholder) {
      case "amount":
        formatted = formatWithDelimiters(number, 2, ",", ".");
        break;
      case "amount_no_decimals":
        formatted = formatWithDelimiters(number, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        formatted = formatWithDelimiters(number, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formatted = formatWithDelimiters(number, 0, ".", ",");
        break;
      default:
        formatted = formatWithDelimiters(number, 2, ",", ".");
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  function getMoneyFormatFromRoot(el) {
    const root = el.closest(ROOT_SEL);
    return root?.getAttribute("data-money-format") || "${{amount}}";
  }

  function getDrawerScrollerFrom(el) {
    const root =
      el.closest(".m-cart-drawer") ||
      el.closest("[data-cart-drawer]") ||
      document.querySelector(".m-cart-drawer") ||
      document.querySelector("[data-cart-drawer]");

    if (!root) return null;

    return (
      root.querySelector(".m-cart-drawer__content") ||
      root.querySelector(".m-cart-drawer__body") ||
      root.querySelector(".m-cart-drawer__inner") ||
      root
    );
  }

  function preserveDrawerScroll(anchorEl, fn) {
    const scroller = getDrawerScrollerFrom(anchorEl);
    const top = scroller ? scroller.scrollTop : 0;

    const restore = () => {
      if (!scroller) return;
      scroller.scrollTop = top;
    };

    return Promise.resolve()
      .then(fn)
      .finally(() => {
        restore();
        requestAnimationFrame(restore);
        setTimeout(restore, 80);
        setTimeout(restore, 250);
        setTimeout(restore, 600);
      });
  }

  let __cfgfMoTimer = null;

  var __smoothFallback = null;
  var __smoothOldDd = [];

  function debounceSoftInit() {
    clearTimeout(__smoothFallback);
    if (__smoothOldDd.length) {
      __smoothOldDd.forEach((dd) => {
        if (dd.isConnected) return;
        if (dd.__cfgfAbort) {
          try {
            dd.__cfgfAbort.abort();
          } catch (_) {}
          dd.__cfgfAbort = null;
        }
        dd.__cfgfSelectRef = null;
      });
      __smoothOldDd = [];
    }
    clearTimeout(__cfgfMoTimer);
    __cfgfMoTimer = setTimeout(() => {
      initAll();
    }, 80);
  }

  function startObserver() {
    const drawer =
      document.querySelector(".m-cart-drawer") ||
      document.querySelector("[data-cart-drawer]");
    if (!drawer) return;

    if (drawer.__cfgfObserver) return;

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== "childList") continue;

        const nodes = [...m.addedNodes, ...m.removedNodes].filter(
          (n) => n && n.nodeType === 1
        );

        const touchedModule =
          nodes.some((n) => n.matches?.(ROOT_SEL) || n.querySelector?.(ROOT_SEL)) ||
          nodes.some(
            (n) =>
              n.matches?.("[data-cfgf-variant-select]") ||
              n.querySelector?.("[data-cfgf-variant-select]")
          );

        if (touchedModule) {
          debounceSoftInit();
          break;
        }
      }
    });

    mo.observe(drawer, { childList: true, subtree: true });
    drawer.__cfgfObserver = mo;
  }

  function startBodyWatcher() {
    if (document.body.__cfgfBodyObserver) return;

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== "childList") continue;

        const nodes = [...m.addedNodes].filter((n) => n && n.nodeType === 1);
        if (!nodes.length) continue;

        const drawerAdded = nodes.some(
          (n) =>
            n.matches?.(".m-cart-drawer") ||
            n.matches?.("[data-cart-drawer]") ||
            n.querySelector?.(".m-cart-drawer") ||
            n.querySelector?.("[data-cart-drawer]")
        );

        if (drawerAdded) {
          setTimeout(() => {
            startObserver();
            initAll();
          }, 0);
          break;
        }
      }
    });

    mo.observe(document.body, { childList: true, subtree: true });
    document.body.__cfgfBodyObserver = mo;
  }

  async function getCartJson() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("Cart fetch failed");
    return res.json();
  }

  // ЗМІНА 1: повертає масив усіх подарунків (замість одного)
  function getCurrentGiftsFromCart(cart) {
    return (cart.items || []).filter(
      (line) => line.properties && line.properties._cfgf_free_gift === "true"
    );
  }

  function getCurrentGiftsFromRoot(root) {
    const variantIdsRaw =
      root.getAttribute("data-cfgf-current-variant-ids") ||
      root.getAttribute("data-cfgf-current-variant-id") ||
      "";
    const lineKeysRaw =
      root.getAttribute("data-cfgf-current-line-keys") ||
      root.getAttribute("data-cfgf-current-line-key") ||
      "";

    const variantIds = variantIdsRaw.split(",").map((id) => Number(id)).filter(Boolean);
    const lineKeys = lineKeysRaw.split(",");

    return variantIds.map((variantId, index) => ({
      variant_id: variantId,
      key: lineKeys[index] || "",
    }));
  }

  function setButtonState(btn, state) {
    if (!btn) return;

    btn.classList.remove("is-added", "is-disabled", "is-loading");
    btn.removeAttribute("disabled");
    btn.dataset.cfgfState = state;

    if (state === "added") {
      btn.classList.add("is-added");
      btn.textContent = "Remove";
      return;
    }

    if (state === "disabled") {
      btn.classList.add("is-disabled");
      btn.textContent = "Add free";
      btn.setAttribute("disabled", "disabled");
      return;
    }

    btn.textContent = "Add free";
  }

  // ЗМІНА 2: враховує data-cfgf-max і масив подарунків
  function syncAllGiftStates(cart) {
    const roots = document.querySelectorAll(ROOT_SEL);
    if (!roots.length) return;

    roots.forEach((root) => {
      const unlocked = root.getAttribute("data-cfgf-unlocked") === "true";
      const max = parseInt(root.getAttribute("data-cfgf-max") || "1", 10);
      const currentGifts = cart ? getCurrentGiftsFromCart(cart) : getCurrentGiftsFromRoot(root);
      const addedVids = new Set(currentGifts.map((g) => Number(g.variant_id)));
      const giftFull = currentGifts.length >= max;
      const buttons = qa(root, "[data-cfgf-add]");

      if (currentGifts.length > 0) {
        root.setAttribute("data-cfgf-current-variant-id", String(currentGifts[0].variant_id));
        root.setAttribute("data-cfgf-current-line-key", currentGifts[0].key || "");
        root.setAttribute("data-cfgf-current-variant-ids", currentGifts.map((gift) => gift.variant_id).join(","));
        root.setAttribute("data-cfgf-current-line-keys", currentGifts.map((gift) => gift.key || "").join(","));
      } else {
        root.setAttribute("data-cfgf-current-variant-id", "");
        root.setAttribute("data-cfgf-current-line-key", "");
        root.setAttribute("data-cfgf-current-variant-ids", "");
        root.setAttribute("data-cfgf-current-line-keys", "");
      }

      buttons.forEach((btn) => {
        const vid = Number(btn.getAttribute("data-vid"));

        if (addedVids.has(vid)) {
          setButtonState(btn, "added");
          const gift = currentGifts.find((g) => Number(g.variant_id) === vid);
          if (gift) btn.setAttribute("data-cfgf-line-key", gift.key || "");
          return;
        }

        if (giftFull || !unlocked) {
          setButtonState(btn, "disabled");
          return;
        }

        setButtonState(btn, "idle");
      });
    });
  }

  async function addGift(variantId) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          _cfgf_free_gift: "true",
          _cfgf_source: "cart_free_gifts",
        },
      }),
    });

    if (!res.ok) throw new Error("Add free gift failed");
    return res.json();
  }

  async function removeGiftByKey(lineKey) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
      }),
    });

    if (!res.ok) throw new Error("Remove free gift failed");
    return res.json();
  }

  document.addEventListener("DOMContentLoaded", () => {
    initAll();
    startObserver();
    startBodyWatcher();
  });

  // Smooth updates on: do not rebuild the old drawer right away (that made the
  // carousel jump before the new HTML arrived); the drawer observer re-inits once
  // the new HTML is in. Fallback: full rebuild if no new HTML comes within 1.5s.
  function onCartRefreshEvent() {
    if (!(window.LamelCartSmooth && window.LamelCartSmooth.enabled === true)) {
      setTimeout(reinitWithRetries, 0);
      return;
    }
    __smoothOldDd = Array.from(document.querySelectorAll("[data-cfgf-dd]"));
    clearTimeout(__smoothFallback);
    __smoothFallback = setTimeout(() => {
      __smoothOldDd = [];
      reinitWithRetries();
    }, 1500);
  }
  document.addEventListener("cart:refresh", onCartRefreshEvent);
  document.documentElement.addEventListener("cart:refresh", onCartRefreshEvent);

  document.addEventListener("cart:refresh", () => setTimeout(startObserver, 0));
  document.documentElement.addEventListener("cart:refresh", () => setTimeout(startObserver, 0));

  document.addEventListener("click", (e) => {
    const prevBtn = e.target.closest(PREV_SEL);
    const nextBtn = e.target.closest(NEXT_SEL);
    if (!prevBtn && !nextBtn) return;

    const root = (prevBtn || nextBtn).closest(ROOT_SEL);
    const track = q(root, TRACK_SEL);
    if (!track) return;

    const n = track.__cfgfRealCount || 0;
    if (n < 2) return;

    track.__cfgfStep = getStep(track);

    const idx = getNearestIndex(track);
    const target = nextBtn ? idx + 1 : idx - 1;

    track.__cfgfLock = true;
    goToIndex(track, target, true);

    afterScrollEnd(track, () => {
      normalizeIfOnClone(track);
      setScrollBehavior(track, "smooth");
      track.__cfgfLock = false;
    });
  });

  document.addEventListener("change", (e) => {
    const sel = e.target.closest("[data-cfgf-variant-select]");
    if (!sel) return;

    const card = sel.closest(".cfgf-cart-gifts__card");
    if (!card) return;

    const opt = sel.options[sel.selectedIndex];
    const vid = Number(opt.value);
    if (!vid) return;

    const btn = card.querySelector("[data-cfgf-add]");
    if (btn) btn.setAttribute("data-vid", String(vid));

    const imageWrap = card.querySelector(".cfgf-cart-gifts__image-wrap");
    const newSrc = opt.getAttribute("data-image");
    const newAlt = opt.getAttribute("data-image-alt") || "";

    if (imageWrap && newSrc) {
      const anchor = imageWrap.querySelector("a");
      const oldImg = imageWrap.querySelector("img");

      if (oldImg) {
        oldImg.src = newSrc;
        oldImg.alt = newAlt;
        oldImg.removeAttribute("srcset");
        oldImg.removeAttribute("sizes");
      } else if (anchor) {
        const img = document.createElement("img");
        img.className = "cfgf-cart-gifts__image";
        img.src = newSrc;
        img.alt = newAlt;
        img.loading = "lazy";
        img.decoding = "async";
        anchor.appendChild(img);
      }
    }

    const priceWrap = card.querySelector("[data-cfgf-price]");
    if (priceWrap) {
      const price = Number(opt.getAttribute("data-price") || 0);
      const compare = Number(opt.getAttribute("data-compare") || 0);
      const moneyFormat = getMoneyFormatFromRoot(sel);

      let compareHtml = "";
      if (compare > price && compare > 0) {
        compareHtml = `<span class="cfgf-cart-gifts__price-compare">${formatMoney(compare, moneyFormat)}</span>`;
      } else if (price > 0) {
        compareHtml = `<span class="cfgf-cart-gifts__price-compare">${formatMoney(price, moneyFormat)}</span>`;
      }

      priceWrap.innerHTML = `
        <span class="cfgf-cart-gifts__price-free">Free</span>
        ${compareHtml}
      `;
    }

    syncAllGiftStates();
  });

  // ЗМІНА 3: клік-хендлер враховує max і масив
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-cfgf-add]");
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

    if (btn.__cfgfBusy) return;
    if (btn.disabled) return;

    const state = btn.dataset.cfgfState || "idle";
    const root = btn.closest(ROOT_SEL);
    if (!root) return;

    btn.__cfgfBusy = true;
    btn.classList.add("is-loading");

    try {
      const cart = await getCartJson();
      const currentGifts = getCurrentGiftsFromCart(cart);
      const max = parseInt(root.getAttribute("data-cfgf-max") || "1", 10);

      if (state === "added") {
        const lineKey =
          btn.getAttribute("data-cfgf-line-key") ||
          root.getAttribute("data-cfgf-current-line-key") ||
          (currentGifts.length > 0 ? currentGifts[0].key : "");

        if (!lineKey) throw new Error("Free gift line key not found");

        await removeGiftByKey(lineKey);
      } else {
        if (currentGifts.length >= max) {
          throw new Error("Max free gifts reached");
        }

        const vid = Number(btn.getAttribute("data-vid"));
        if (!vid) throw new Error("Variant id missing");

        await addGift(vid);
      }

      await preserveDrawerScroll(btn, () => {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh"));
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        if (window.MinimogEvents?.publish) {
          window.MinimogEvents.publish("cart:updated", {});
        }
      });

      setTimeout(syncAllGiftStates, 80);
      setTimeout(syncAllGiftStates, 350);
    } catch (err) {
      console.error(err);
      btn.classList.remove("is-loading");
    } finally {
      setTimeout(() => {
        btn.__cfgfBusy = false;
        btn.classList.remove("is-loading");
      }, 200);
    }
  });
})();